import React from 'react';
import Modal from '../../../../components/Modal';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { FriendLocation, FriendGroupWithMembers, TemporaryLocationFormData, LocationType } from '../../../../types';
import { useTheme } from '../../../../contexts/ThemeContext';

interface LocationTypeConfig {
  value: LocationType;
  label: string;
  icon: string;
  color: string;
}

interface TempLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tempLocationData: TemporaryLocationFormData;
  setTempLocationData: React.Dispatch<React.SetStateAction<TemporaryLocationFormData>>;
  addressSearchQuery: string;
  setAddressSearchQuery: (query: string) => void;
  isSearchingAddress: boolean;
  addressSuggestions: any[];
  selectAddress: (suggestion: any) => void;
  showCoordinates: boolean;
  setShowCoordinates: (show: boolean) => void;
  LOCATION_TYPES: LocationTypeConfig[];
  groups: FriendGroupWithMembers[];
  showBlacklist: boolean;
  setShowBlacklist: (show: boolean) => void;
  blacklistSearchQuery: string;
  setBlacklistSearchQuery: (query: string) => void;
  locations: FriendLocation[];
  friendGroups: Map<number, { id: number; name: string; color?: string }[]>;
  blacklistedFriendIds: number[];
  setBlacklistedFriendIds: (ids: number[]) => void;
  dateMode: 'day' | 'datetime';
  setDateMode: (mode: 'day' | 'datetime') => void;
  applyNowTemplate: () => void;
  applyTonightTemplate: () => void;
  applyWeekendTemplate: () => void;
  formatLocalDateTime: (date: Date) => string;
  CustomDateInput: React.ComponentType<any>;
  onSave: () => void;
  onDelete?: () => void;
  showCreateGroupInTempModal: boolean;
  setShowCreateGroupInTempModal: (show: boolean) => void;
  newGroupDataInTempModal: { name: string; color: string };
  setNewGroupDataInTempModal: (data: { name: string; color: string }) => void;
  onCreateGroup: () => void;
  loading: boolean;
  t: (key: string, params?: any) => string;
}

/**
 * Modal for creating and editing temporary locations
 */
export const TempLocationModal: React.FC<TempLocationModalProps> = ({
  isOpen,
  onClose,
  tempLocationData,
  setTempLocationData,
  onDelete,
  addressSearchQuery,
  setAddressSearchQuery,
  isSearchingAddress,
  addressSuggestions,
  selectAddress,
  showCoordinates,
  setShowCoordinates,
  LOCATION_TYPES,
  groups,
  showBlacklist,
  setShowBlacklist,
  blacklistSearchQuery,
  setBlacklistSearchQuery,
  locations,
  friendGroups,
  blacklistedFriendIds,
  setBlacklistedFriendIds,
  dateMode,
  setDateMode,
  applyNowTemplate,
  applyTonightTemplate,
  applyWeekendTemplate,
  formatLocalDateTime,
  CustomDateInput,
  onSave,
  showCreateGroupInTempModal,
  setShowCreateGroupInTempModal,
  newGroupDataInTempModal,
  setNewGroupDataInTempModal,
  onCreateGroup,
  loading,
  t,
}) => {
  const { theme } = useTheme();

  // Log address when modal opens or when save is clicked
  React.useEffect(() => {
    if (isOpen) {
      console.log('📍 TempLocationModal opened with addressSearchQuery:', addressSearchQuery);
    }
  }, [isOpen, addressSearchQuery]);

  const handleSave = () => {
    console.log('💾 Saving temp location with address:', addressSearchQuery);
    onSave();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setBlacklistedFriendIds([]);
      }}
      title={tempLocationData.id ? t('map.form.editTitle') : t('map.form.createTitle')}
      titleExtra={
        <div
          className="toggle-switch"
          onClick={() => setTempLocationData({ ...tempLocationData, is_active: tempLocationData.is_active !== false ? false : true })}
          style={{
            backgroundColor: tempLocationData.is_active !== false ? '#28a745' : '#dc3545',
            cursor: 'pointer',
            width: '50px',
            height: '26px',
            borderRadius: '13px',
            position: 'relative',
            transition: 'background-color 0.3s',
          }}
        >
          <div
            className="toggle-switch-slider"
            style={{
              position: 'absolute',
              top: '3px',
              left: tempLocationData.is_active !== false ? '26px' : '3px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: 'white',
              transition: 'left 0.3s',
            }}
          />
        </div>
      }
      footer={
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', width: '100%' }}>
          {tempLocationData.id && onDelete && (
            <button
              className="directions-button"
              onClick={onDelete}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
              }}
            >
              <span className="button-text-desktop">Supprimer</span>
              <span className="button-text-mobile">Supprimer</span>
            </button>
          )}
          <button className="directions-button temp-location-save-button" onClick={handleSave}>
            <span className="button-icon button-icon-desktop">
              {tempLocationData.id ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', verticalAlign: 'middle' }}>
                  <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              ) : (
                "📌"
              )}
            </span>
            <span className="button-text-desktop">{tempLocationData.id ? t('map.form.editButton') : t('map.form.createButton')}</span>
            <span className="button-text-mobile">Sauvegarder adresse</span>
          </button>
        </div>
      }
    >
      <div className="temp-location-form">
        <div className="form-group">
          <label htmlFor="address-search">🔍 Adresse de ton lieu temporaire</label>
          <div className="address-search-container">
            <input
              id="address-search"
              type="text"
              value={addressSearchQuery}
              onChange={(e) => setAddressSearchQuery(e.target.value)}
              placeholder={t('map.form.addressPlaceholder')}
              className="form-input"
            />
            {isSearchingAddress && <div className="search-spinner">🔄</div>}
            {addressSuggestions.length > 0 && (
              <div className="address-suggestions">
                {addressSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="address-suggestion-item"
                    onClick={() => selectAddress(suggestion)}
                  >
                    <span className="suggestion-icon">📍</span>
                    <div className="suggestion-content">
                      <div className="suggestion-name">
                        {suggestion.name || suggestion.display_name.split(',')[0]}
                      </div>
                      <div className="suggestion-address">{suggestion.display_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="address-coordinates-row">
          <div
            className="coordinates-toggle-link-right"
            onClick={() => setShowCoordinates(!showCoordinates)}
          >
            {t('map.form.verifyCoordinates')}
          </div>

          {showCoordinates && (
            <div className="coordinates-edit-container">
              <div className="coordinates-inputs">
                <input
                  type="number"
                  step="0.000001"
                  value={tempLocationData.latitude || ''}
                  onChange={(e) => setTempLocationData({ ...tempLocationData, latitude: parseFloat(e.target.value) || 0 })}
                  placeholder="Latitude"
                  className="coordinate-input"
                />
                <input
                  type="number"
                  step="0.000001"
                  value={tempLocationData.longitude || ''}
                  onChange={(e) => setTempLocationData({ ...tempLocationData, longitude: parseFloat(e.target.value) || 0 })}
                  placeholder="Longitude"
                  className="coordinate-input"
                />
              </div>
              <button
                type="button"
                className="copy-coordinates-btn"
                onClick={() => {
                  const coords = `${tempLocationData.latitude},${tempLocationData.longitude}`;
                  navigator.clipboard.writeText(coords);
                  alert('📋 Coordonnées copiées ! Collez-les dans Google Maps.');
                }}
                title="Copier les coordonnées (format Google Maps)"
              >
                📋
              </button>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="temp-loc-name">Donne un titre ! *</label>
          <input
            id="temp-loc-name"
            type="text"
            value={tempLocationData.name || ''}
            onChange={(e) => setTempLocationData({ ...tempLocationData, name: e.target.value })}
            placeholder="Nom du lieu ? Café Cherry, Chez mon pote Chab, ..."
            className="form-input"
          />
        </div>

        <div className="form-group">
          <textarea
            id="temp-loc-desc"
            value={tempLocationData.description || ''}
            onChange={(e) => setTempLocationData({ ...tempLocationData, description: e.target.value })}
            placeholder="Description... En déplacement pro ? Dispo en soirée ? Une soirée entre amis ?"
            className="form-textarea"
            rows={3}
          />
        </div>

        <div className="form-group">
          <div className="location-type-selector-inline">
            {LOCATION_TYPES.map((locationType) => (
              <button
                key={locationType.value}
                type="button"
                className={`location-type-button-inline ${tempLocationData.type === locationType.value ? 'selected' : ''}`}
                style={{
                  backgroundColor: tempLocationData.type === locationType.value ? locationType.color : 'transparent',
                  borderColor: locationType.color,
                  color: tempLocationData.type === locationType.value ? 'white' : locationType.color,
                }}
                onClick={() => setTempLocationData({ ...tempLocationData, type: locationType.value })}
              >
                <span className="location-type-icon">{locationType.icon}</span>
                <span className="location-type-label">{locationType.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Visibilité Section */}
        <div className="form-section-separator">
          <div className="separator-line"></div>
          <span className="separator-text">Visibilité</span>
          <div className="separator-line"></div>
        </div>

        <div className="form-group">
          <div className="segmented-control">
            <button
              type="button"
              className={`segmented-control-button ${(!tempLocationData.visibility_type || tempLocationData.visibility_type === 'all') ? 'active' : ''}`}
              onClick={() => {
                setTempLocationData({ ...tempLocationData, visibility_type: 'all', visible_group_ids: [], visible_friend_ids: [] });
              }}
            >
              Tout mon réseau
            </button>
            <button
              type="button"
              className={`segmented-control-button ${tempLocationData.visibility_type === 'groups' ? 'active' : ''}`}
              onClick={() => {
                setTempLocationData({ ...tempLocationData, visibility_type: 'groups', visible_friend_ids: [] });
              }}
            >
              Groupes spécifiques
            </button>
          </div>
          {tempLocationData.visibility_type === 'groups' && (
            <div className="groups-selector">
              <p className="selector-hint">{t('map.form.groupsSelectorHint')}</p>
              {groups.length > 0 ? (
                <>
                  <div className="groups-list">
                    {groups.map((group) => (
                      <label key={group.id} className="group-checkbox-item">
                        <input
                          type="checkbox"
                          checked={tempLocationData.visible_group_ids?.includes(group.id) || false}
                          onChange={(e) => {
                            const groupIds = tempLocationData.visible_group_ids || [];
                            if (e.target.checked) {
                              setTempLocationData({
                                ...tempLocationData,
                                visible_group_ids: [...groupIds, group.id]
                              });
                            } else {
                              setTempLocationData({
                                ...tempLocationData,
                                visible_group_ids: groupIds.filter(id => id !== group.id)
                              });
                            }
                          }}
                        />
                        <span className="group-name" style={{ color: group.color || '#4a90e2' }}>
                          {group.name}
                        </span>
                        <span className="group-member-count">({group.members.length} {group.members.length > 1 ? t('friends.groups.members') : t('friends.groups.member')})</span>
                      </label>
                    ))}
                  </div>

                  {/* Create new group button/form */}
                  {!showCreateGroupInTempModal ? (
                    <button
                      type="button"
                      className="btn-create-group-inline"
                      onClick={() => setShowCreateGroupInTempModal(true)}
                    >
                      + Créer un nouveau groupe
                    </button>
                  ) : (
                    <div className="inline-group-create-form">
                      <input
                        type="text"
                        placeholder="Nom du groupe..."
                        value={newGroupDataInTempModal.name}
                        onChange={(e) => setNewGroupDataInTempModal({ ...newGroupDataInTempModal, name: e.target.value })}
                        className="inline-group-name-input"
                      />
                      <input
                        type="color"
                        value={newGroupDataInTempModal.color}
                        onChange={(e) => setNewGroupDataInTempModal({ ...newGroupDataInTempModal, color: e.target.value })}
                        className="inline-color-input"
                        title="Choisir la couleur"
                      />
                      <button
                        className="btn-inline-create"
                        onClick={onCreateGroup}
                        disabled={loading || !newGroupDataInTempModal.name.trim()}
                        title="Créer le groupe"
                      >
                        {loading ? '...' : '✓'}
                      </button>
                      <button
                        className="btn-inline-cancel"
                        onClick={() => {
                          setShowCreateGroupInTempModal(false);
                          setNewGroupDataInTempModal({ name: '', color: '#4a90e2' });
                        }}
                        disabled={loading}
                        title="Annuler"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="no-groups-message">{t('map.form.noGroupsMessage')}</p>

                  {/* Create new group button/form when no groups exist */}
                  {!showCreateGroupInTempModal ? (
                    <button
                      type="button"
                      className="btn-create-group-inline"
                      onClick={() => setShowCreateGroupInTempModal(true)}
                    >
                      + Créer un nouveau groupe
                    </button>
                  ) : (
                    <div className="inline-group-create-form">
                      <input
                        type="text"
                        placeholder="Nom du groupe..."
                        value={newGroupDataInTempModal.name}
                        onChange={(e) => setNewGroupDataInTempModal({ ...newGroupDataInTempModal, name: e.target.value })}
                        className="inline-group-name-input"
                      />
                      <input
                        type="color"
                        value={newGroupDataInTempModal.color}
                        onChange={(e) => setNewGroupDataInTempModal({ ...newGroupDataInTempModal, color: e.target.value })}
                        className="inline-color-input"
                        title="Choisir la couleur"
                      />
                      <button
                        className="btn-inline-create"
                        onClick={onCreateGroup}
                        disabled={loading || !newGroupDataInTempModal.name.trim()}
                        title="Créer le groupe"
                      >
                        {loading ? '...' : '✓'}
                      </button>
                      <button
                        className="btn-inline-cancel"
                        onClick={() => {
                          setShowCreateGroupInTempModal(false);
                          setNewGroupDataInTempModal({ name: '', color: '#4a90e2' });
                        }}
                        disabled={loading}
                        title="Annuler"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <div className="toggle-switch-container">
            <span className="toggle-switch-label">🚫 Cacher à certains contacts</span>
            <div
              className={`toggle-switch ${showBlacklist ? 'active' : ''}`}
              onClick={() => setShowBlacklist(!showBlacklist)}
            >
              <div className="toggle-switch-slider"></div>
            </div>
          </div>

          {showBlacklist && (
            <>
              <div style={{ marginTop: '15px', marginBottom: '10px' }}>
                <input
                  type="text"
                  placeholder={t('map.form.searchFriendPlaceholder')}
                  value={blacklistSearchQuery}
                  onChange={(e) => setBlacklistSearchQuery(e.target.value)}
                  className="form-input"
                  style={{ marginBottom: '10px' }}
                />
              </div>
              {locations.length > 0 ? (
                <div className="blacklist-friends-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {locations
                    .filter(friend => {
                      // Search filter
                      const matchesSearch = blacklistSearchQuery === '' ||
                        `${friend.first_name} ${friend.last_name}`.toLowerCase().includes(blacklistSearchQuery.toLowerCase());

                      // Group filter (only when "Groupes spécifiques" is selected)
                      if (tempLocationData.visibility_type === 'groups' && tempLocationData.visible_group_ids && tempLocationData.visible_group_ids.length > 0) {
                        const friendGroupIds = friendGroups.get(friend.id)?.map(g => g.id) || [];
                        const isInSelectedGroups = tempLocationData.visible_group_ids.some(groupId => friendGroupIds.includes(groupId));
                        return matchesSearch && isInSelectedGroups;
                      }

                      return matchesSearch;
                    })
                    .map((friend) => {
                      const initials = `${friend.first_name.charAt(0)}${friend.last_name.charAt(0)}`.toUpperCase();
                      const photoUrl = friend.profile_photo
                        ? `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000'}${friend.profile_photo}`
                        : null;

                      return (
                        <label key={friend.id} className="friend-blacklist-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          marginBottom: '5px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: blacklistedFriendIds.includes(friend.id) ? '#ffe0e0' : '#f5f5f5',
                          transition: 'all 0.2s ease',
                          gap: '12px',
                        }}>
                          <input
                            type="checkbox"
                            checked={blacklistedFriendIds.includes(friend.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBlacklistedFriendIds([...blacklistedFriendIds, friend.id]);
                              } else {
                                setBlacklistedFriendIds(blacklistedFriendIds.filter(id => id !== friend.id));
                              }
                            }}
                            style={{
                              flexShrink: 0,
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                            }}
                          />
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: photoUrl ? 'transparent' : '#4a90e2',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            overflow: 'hidden',
                          }}>
                            {photoUrl ? (
                              <img
                                src={photoUrl}
                                alt={`${friend.first_name} ${friend.last_name}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              initials
                            )}
                          </div>
                          <span style={{
                            fontSize: '15px',
                            fontWeight: '500',
                            flex: 1,
                          }}>
                            {friend.first_name} {friend.last_name}
                          </span>
                        </label>
                      );
                    })}
                  {locations.filter(friend => {
                    // Search filter
                    const matchesSearch = blacklistSearchQuery === '' ||
                      `${friend.first_name} ${friend.last_name}`.toLowerCase().includes(blacklistSearchQuery.toLowerCase());

                    // Group filter (only when "Groupes spécifiques" is selected)
                    if (tempLocationData.visibility_type === 'groups' && tempLocationData.visible_group_ids && tempLocationData.visible_group_ids.length > 0) {
                      const friendGroupIds = friendGroups.get(friend.id)?.map(g => g.id) || [];
                      const isInSelectedGroups = tempLocationData.visible_group_ids.some(groupId => friendGroupIds.includes(groupId));
                      return matchesSearch && isInSelectedGroups;
                    }

                    return matchesSearch;
                  }).length === 0 && (
                    <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                      Aucun ami trouvé
                    </p>
                  )}
                </div>
              ) : (
                <p className="no-groups-message">{t('map.form.noFriendsYet')}</p>
              )}
              {blacklistedFriendIds.length > 0 && (
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                  {t('map.form.friendsExcluded', { count: blacklistedFriendIds.length })}
                </p>
              )}
            </>
          )}
        </div>

        {/* Separator for dates section */}
        <div className="form-section-separator">
          <div className="separator-line"></div>
          <span className="separator-text">{t('map.form.datesSection')}</span>
          <div className="separator-line"></div>
        </div>

        {/* Quick templates buttons (inline on mobile only, below separator) */}
        <div className="prefill-buttons-mobile-only">
          <button type="button" className="prefill-button prefill-button-mobile" onClick={applyNowTemplate}>
            {dateMode === 'day' ? t('map.form.prefillToday') : `⏱️ ${t('map.form.prefillNow')}`}
          </button>
          <button type="button" className="prefill-button prefill-button-mobile" onClick={applyTonightTemplate}>
            {dateMode === 'day' ? `📆 ${t('map.form.prefillThisWeek')}` : `🌙 ${t('map.form.prefillTonight')}`}
          </button>
          <button type="button" className="prefill-button prefill-button-mobile" onClick={applyWeekendTemplate}>
            🎉 {t('map.form.prefillWeekend')}
          </button>
        </div>

        {/* Date mode toggle switch */}
        <div className="form-group">
          <div className="toggle-switch-container">
            <span className="toggle-switch-label">⌚ Toute la journée</span>
            <div
              className={`toggle-switch ${dateMode === 'day' ? 'active' : ''}`}
              onClick={() => setDateMode(dateMode === 'day' ? 'datetime' : 'day')}
            >
              <div className="toggle-switch-slider"></div>
            </div>
          </div>
        </div>

        {/* Dates inputs with prefill column on desktop */}
        <div className="dates-with-prefill-container">
          <div className="dates-inputs-column">
            <div className="form-group">
              <DatePicker
                selected={tempLocationData.start_datetime ? new Date(tempLocationData.start_datetime) : null}
                onChange={(date: Date | null) => {
                  if (date) {
                    const formatted = dateMode === 'day'
                      ? format(date, 'yyyy-MM-dd')
                      : formatLocalDateTime(date);
                    setTempLocationData({ ...tempLocationData, start_datetime: formatted });
                  }
                }}
                showTimeSelect={dateMode === 'datetime'}
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat={dateMode === 'day' ? "EEE dd MMM yyyy" : "EEE dd MMM yyyy  HH:mm"}
                locale="fr"
                customInput={<CustomDateInput />}
                placeholderText={dateMode === 'day' ? "Sélectionner une date" : "Sélectionner date et heure"}
              />
            </div>

            <div className="form-group">
              <DatePicker
                selected={tempLocationData.end_datetime ? new Date(tempLocationData.end_datetime) : null}
                onChange={(date: Date | null) => {
                  if (date) {
                    const formatted = dateMode === 'day'
                      ? format(date, 'yyyy-MM-dd')
                      : formatLocalDateTime(date);
                    setTempLocationData({ ...tempLocationData, end_datetime: formatted });
                  }
                }}
                showTimeSelect={dateMode === 'datetime'}
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat={dateMode === 'day' ? "EEE dd MMM yyyy" : "EEE dd MMM yyyy  HH:mm"}
                locale="fr"
                customInput={<CustomDateInput />}
                placeholderText={dateMode === 'day' ? "Sélectionner une date" : "Sélectionner date et heure"}
                minDate={tempLocationData.start_datetime ? new Date(tempLocationData.start_datetime) : undefined}
              />
            </div>
          </div>

          {/* Desktop prefill buttons column */}
          <div className="prefill-buttons-column">
            <label className="prefill-label">{t('map.form.quickTemplates')}</label>
            <button type="button" className="prefill-button" onClick={applyNowTemplate}>
              {dateMode === 'day' ? '📅' : '⏱️'} {dateMode === 'day' ? t('map.form.prefillToday') : t('map.form.prefillNow')}
            </button>
            <button type="button" className="prefill-button" onClick={applyTonightTemplate}>
              {dateMode === 'day' ? '📆' : '🌙'} {dateMode === 'day' ? t('map.form.prefillThisWeek') : t('map.form.prefillTonight')}
            </button>
            <button type="button" className="prefill-button" onClick={applyWeekendTemplate}>
              🎉 {t('map.form.prefillWeekend')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
